import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtNum, fmtCur, fmtDate, downloadExport } from "@/lib/api";
import CustomerTable from "@/components/CustomerTable";
import PeriodFilter from "@/components/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Download, Users, ShieldCheck, Clock, DollarSign, FileText, Receipt, Loader2, Percent, Wallet } from "lucide-react";
import { toast } from "sonner";

const fmtAmt = (v, cur) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${n} ${cur}` : n;
};
const fmtMoney = (v) => "$" + Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function monthBounds(ym) {
  // ym = "2026-05" -> ["2026-05-01", "2026-05-31"]
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, "0")}`];
}

export default function ReferralDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [month, setMonth] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["referral-customers", code],
    queryFn: () => api.get("/customers", { params: { referral: code, limit: 500 } }).then((r) => r.data),
  });
  const { data: statement, isLoading: stLoading, isError: stError } = useQuery({
    queryKey: ["referral-statement", code, dateFrom, dateTo],
    queryFn: () => api.get(`/referrals/${encodeURIComponent(code)}/statement`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data),
    retry: false,
  });

  const ownerName = statement?.name || "";
  const rows = data?.rows || [];
  const verified = rows.filter((r) => r.kyc_verified).length;
  const deposits = rows.reduce((s, r) => s + (r.deposits || 0), 0);
  const t = statement?.totals;
  const txs = statement?.transactions || [];

  const setPeriod = (f, to) => { setDateFrom(f); setDateTo(to); setMonth(""); };
  const onMonth = (ym) => {
    setMonth(ym);
    if (!ym) { setDateFrom(""); setDateTo(""); return; }
    const [f, to] = monthBounds(ym);
    setDateFrom(f);
    setDateTo(to);
  };

  const stats = [
    { label: "Clients", value: fmtNum(data?.total ?? 0), icon: Users },
    { label: "Verified", value: fmtNum(verified), icon: ShieldCheck },
    { label: "Pending", value: fmtNum((data?.total ?? 0) - verified), icon: Clock },
    { label: "Total Deposits", value: fmtCur(deposits), icon: DollarSign },
  ];

  const earnStats = t ? [
    { label: "Completed Tx", value: fmtNum(t.completed), icon: Receipt, testId: "stat-completed-tx" },
    { label: "Volume", value: fmtMoney(t.volume), icon: DollarSign, testId: "stat-volume" },
    { label: "Fees Generated", value: fmtMoney(t.fee_revenue), icon: Wallet, testId: "stat-fees" },
    { label: `To Pay (${statement.rate?.toFixed(0)}%)`, value: fmtMoney(t.commission), icon: Percent, highlight: true, testId: "stat-commission" },
  ] : [];

  const handleExport = async () => {
    try {
      await downloadExport({ referral: code }, "csv");
      toast.success("Export started");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const handleStatementPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/referrals/${encodeURIComponent(code)}/statement/pdf`, {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement_${code}_${dateFrom || "all"}_${dateTo || "now"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statement PDF downloaded");
    } catch (e) {
      toast.error("Statement export failed");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="referral-detail-page">
      <button
        onClick={() => navigate("/referrals")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="back-to-referrals"
      >
        <ArrowLeft className="h-4 w-4" /> Back to referrals
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Referral Code</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight font-mono">{code}</h1>
          {ownerName && <p className="mt-1 text-sm font-medium text-foreground" data-testid="referral-owner-name">{ownerName}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-referral-detail">
            <Download className="mr-2 h-4 w-4" /> Clients CSV
          </Button>
          <Button onClick={handleStatementPdf} disabled={pdfBusy} data-testid="statement-pdf-button">
            {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Statement PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-md border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <s.icon className="h-4 w-4 text-[#2b9bb7]" />
            </div>
            <div className="mt-2 font-heading text-2xl font-semibold tabular">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Earnings statement */}
      <section className="rounded-md border border-border bg-card p-5" data-testid="statement-section">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-heading text-lg font-medium">
            <Wallet className="h-5 w-5 text-[#2b9bb7]" /> Earnings statement
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Month</span>
            <Input type="month" value={month} onChange={(e) => onMonth(e.target.value)} className="w-[160px]" data-testid="statement-month" />
          </div>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Pick a month (or any period) to see the fees this referrer&apos;s clients generated and the commission due. Export it with &quot;Statement PDF&quot;.
        </p>
        <PeriodFilter dateFrom={dateFrom} dateTo={dateTo} onChange={setPeriod} testId="statement-period" />

        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          {stError ? (
            <div className="col-span-2 rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground md:col-span-4" data-testid="statement-error">
              You don&apos;t have access to this referral&apos;s statement.
            </div>
          ) : stLoading
            ? [...Array(4)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-md border border-border bg-[#E6EFF2]" />)
            : earnStats.map((s) => (
                <div key={s.label} className={`rounded-md border p-5 ${s.highlight ? "border-[#326F4B]/40 bg-[#326F4B]/5" : "border-border bg-card"}`} data-testid={s.testId}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{s.label}</span>
                    <s.icon className={`h-4 w-4 ${s.highlight ? "text-[#326F4B]" : "text-[#2b9bb7]"}`} />
                  </div>
                  <div className={`mt-2 font-heading text-2xl font-semibold tabular ${s.highlight ? "text-[#326F4B]" : ""}`}>{s.value}</div>
                </div>
              ))}
        </div>

        {/* Transactions in period */}
        <div className="mt-5">
          <h4 className="mb-2 text-sm font-medium text-muted-foreground">Transactions in period ({fmtNum(txs.length)})</h4>
          {txs.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground" data-testid="statement-tx-empty">
              No transactions from this referrer&apos;s clients in the selected period.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm" data-testid="statement-tx-table">
                <thead>
                  <tr className="border-b border-border bg-[#EDF3F5] text-left">
                    {["Date", "Customer", "Provider", "Amount", "Fee", "Commission", "Status"].map((h, i) => (
                      <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i >= 3 && i <= 5 ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {txs.slice(0, 200).map((x, i) => (
                    <tr key={x.id} className="border-b border-border" data-testid={`statement-tx-row-${i}`}>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(x.date) !== "—" ? fmtDate(x.date) : x.created_day}</td>
                      <td className="px-4 py-3 font-medium">{x.customer || x.account_id || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{x.provider || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(x.amount, x.currency)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(x.fee, x.currency)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular text-[#326F4B]">{fmtMoney(x.commission)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{x.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <div>
        <h3 className="mb-3 font-heading text-lg font-medium">Customers under this referral</h3>
        <CustomerTable rows={rows} loading={isLoading} onRowClick={(c) => c.id && navigate(`/customers/${c.id}`)} />
      </div>
    </div>
  );
}
